import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { agentService } from '../../services/agentService';
import { toolService } from '../../services/toolService';
import { useNotificationStore } from '../../store/notificationStore';
import type { ToolDto } from '../../types/tool';
import { CodeEditor } from '../common/CodeEditor';
import type {
  AgentDefinitionFunctionDto,
  CreateAgentDefinitionFunctionRequest,
  UpdateAgentDefinitionFunctionRequest,
} from '../../types/agent';

interface AgentDefinitionFunctionListProps {
  agentId: string;
  readOnly?: boolean;
  /** Initial list (e.g. from agent detail); if provided, we still refetch on mount for freshness. */
  initialFunctions?: AgentDefinitionFunctionDto[];
}

const DEFAULT_PARAMS_JSON = `{
  "type": "object",
  "properties": {},
  "required": []
}`;

/**
 * List and CRUD for agent-registered functions (OpenAI Assistants style).
 */
export const AgentDefinitionFunctionList = ({
  agentId,
  readOnly = false,
  initialFunctions,
}: AgentDefinitionFunctionListProps) => {
  const [functions, setFunctions] = useState<AgentDefinitionFunctionDto[]>(initialFunctions ?? []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFn, setEditingFn] = useState<AgentDefinitionFunctionDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formParametersJson, setFormParametersJson] = useState(DEFAULT_PARAMS_JSON);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [openCatalogAttachDialog, setOpenCatalogAttachDialog] = useState(false);
  const [catalogToolsPickList, setCatalogToolsPickList] = useState<ToolDto[]>([]);
  const [loadingCatalogTools, setLoadingCatalogTools] = useState(false);
  const [selectedCatalogToolIds, setSelectedCatalogToolIds] = useState<Set<string>>(new Set());
  const [catalogToolSearchTerm, setCatalogToolSearchTerm] = useState('');
  const [attachingCatalogTools, setAttachingCatalogTools] = useState(false);
  const { addNotification } = useNotificationStore();

  const filteredCatalogTools = useMemo(() => {
    if (!catalogToolSearchTerm.trim()) return catalogToolsPickList;
    const q = catalogToolSearchTerm.trim().toLowerCase();
    return catalogToolsPickList.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        (t.displayName ?? '').toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q)
    );
  }, [catalogToolsPickList, catalogToolSearchTerm]);

  const toggleCatalogToolSelection = (toolId: string) => {
    setSelectedCatalogToolIds((prev) => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  };

  const loadFunctions = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getFunctions(agentId);
      setFunctions(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load tools';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFunctions();
  }, [agentId]);

  const handleAdd = () => {
    setEditingFn(null);
    setFormName('');
    setFormDescription('');
    setFormParametersJson(DEFAULT_PARAMS_JSON);
    setFormSortOrder(functions.length);
    setOpenDialog(true);
  };

  const handleEdit = (fn: AgentDefinitionFunctionDto) => {
    setEditingFn(fn);
    setFormName(fn.name);
    setFormDescription(fn.description);
    setFormParametersJson(fn.parametersJson && fn.parametersJson !== '{}' ? fn.parametersJson : DEFAULT_PARAMS_JSON);
    setFormSortOrder(fn.sortOrder);
    setOpenDialog(true);
  };

  const handleDelete = async (fn: AgentDefinitionFunctionDto) => {
    if (!window.confirm(`Delete tool "${fn.name}"?`)) return;
    try {
      await agentService.deleteFunction(agentId, fn.id);
      addNotification('Tool deleted', 'success');
      loadFunctions();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete tool', 'error');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingFn(null);
  };

  const handleOpenAttachCatalog = () => {
    setSelectedCatalogToolIds(new Set());
    setCatalogToolSearchTerm('');
    setOpenCatalogAttachDialog(true);
    setLoadingCatalogTools(true);
    setCatalogToolsPickList([]);
    toolService
      .getTools({ isActive: true, pageNumber: 1, pageSize: 200 })
      .then((page) => {
        const tools = page.items ?? [];
        const available = tools.filter(
          (t) =>
            !functions.some(
              (f) => f.name === t.name || (f.toolId != null && f.toolId === t.id)
            )
        );
        setCatalogToolsPickList(available);
      })
      .catch((err: any) => {
        addNotification(err.response?.data?.message || 'Failed to load catalog tools', 'error');
      })
      .finally(() => setLoadingCatalogTools(false));
  };

  const handleCatalogAttachDialogClose = () => {
    setOpenCatalogAttachDialog(false);
    setSelectedCatalogToolIds(new Set());
    setCatalogToolSearchTerm('');
  };

  const handleAttachCatalogTools = async () => {
    const ids = Array.from(selectedCatalogToolIds);
    if (ids.length === 0) {
      addNotification('Select at least one tool to attach', 'warning');
      return;
    }
    setAttachingCatalogTools(true);
    const failed: string[] = [];
    try {
      for (const toolId of ids) {
        try {
          await agentService.attachCatalogTool(agentId, toolId);
        } catch (err: any) {
          failed.push(toolId);
          addNotification(err.response?.data?.message || `Failed to attach tool ${toolId}`, 'error');
        }
      }
      const succeeded = ids.length - failed.length;
      if (succeeded > 0) {
        addNotification(
          succeeded === 1 ? 'Catalog tool attached' : `${succeeded} catalog tools attached`,
          'success'
        );
        handleCatalogAttachDialogClose();
        loadFunctions();
      }
    } finally {
      setAttachingCatalogTools(false);
    }
  };

  const handleDialogSave = async () => {
    const name = formName.trim();
    const description = formDescription.trim();
    if (!name) {
      addNotification('Name is required', 'warning');
      return;
    }
    if (!description) {
      addNotification('Description is required', 'warning');
      return;
    }
    const paramsJson = formParametersJson.trim() || '{}';
    try {
      JSON.parse(paramsJson);
    } catch {
      addNotification('Parameters must be valid JSON', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingFn) {
        const payload: UpdateAgentDefinitionFunctionRequest = {
          name,
          description,
          parametersJson: paramsJson,
          sortOrder: formSortOrder,
        };
        await agentService.updateFunction(agentId, editingFn.id, payload);
        addNotification('Tool updated', 'success');
      } else {
        const payload: CreateAgentDefinitionFunctionRequest = {
          name,
          description,
          parametersJson: paramsJson,
          sortOrder: formSortOrder,
        };
        await agentService.createFunction(agentId, payload);
        addNotification('Tool added', 'success');
      }
      handleDialogClose();
      loadFunctions();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to save tool', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Registered Tools</Typography>
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
              Add tool
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<BuildIcon />}
              onClick={handleOpenAttachCatalog}
            >
              Attach catalog tool
            </Button>
          </Box>
        )}
      </Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {functions.length === 0 ? (
        <Typography color="text.secondary" paragraph>
          No tools registered. Add tools (name, description, parameters schema) so the agent can call them during conversations, similar to OpenAI Assistants.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} aria-label="Type" />
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Order</TableCell>
                {!readOnly && (
                  <TableCell align="right" width={120}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {functions.map((fn) => (
                <TableRow key={fn.id}>
                  <TableCell sx={{ verticalAlign: 'middle' }} width={48}>
                    {fn.isSystem ? (
                      <BuildIcon fontSize="small" color="action" titleAccess="System tool" />
                    ) : (
                      <CodeIcon fontSize="small" color="action" titleAccess="Custom tool" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{fn.name}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>{fn.description}</TableCell>
                  <TableCell align="right">{fn.sortOrder}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      {!fn.isSystem && (
                        <IconButton size="small" onClick={() => handleEdit(fn)} aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => handleDelete(fn)} color="error" aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingFn ? 'Edit tool' : 'Add tool'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            placeholder="e.g. get_weather"
            fullWidth
            variant="outlined"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            helperText="Unique name the LLM will use to call this function"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            placeholder="What this function does (shown to the LLM)"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Sort order"
            type="number"
            fullWidth
            variant="outlined"
            value={formSortOrder}
            onChange={(e) => setFormSortOrder(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Parameters (JSON schema)
          </Typography>
          <CodeEditor
            value={formParametersJson}
            onChange={setFormParametersJson}
            language="json"
            height="240px"
            readOnly={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingFn ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCatalogAttachDialog} onClose={handleCatalogAttachDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Attach catalog tool</DialogTitle>
        <DialogContent>
          {loadingCatalogTools ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : catalogToolsPickList.length === 0 ? (
            <Typography color="text.secondary">
              No active catalog tools available to attach, or all are already attached to this agent.
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tools..."
                value={catalogToolSearchTerm}
                onChange={(e) => setCatalogToolSearchTerm(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ 'aria-label': 'Search catalog tools' }}
              />
              {filteredCatalogTools.length === 0 ? (
                <Typography color="text.secondary">
                  No tools match your search.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredCatalogTools.map((tool) => (
                    <ListItemButton
                      key={tool.id}
                      selected={selectedCatalogToolIds.has(tool.id)}
                      onClick={() => toggleCatalogToolSelection(tool.id)}
                    >
                      <ListItemText
                        primary={tool.displayName || tool.name}
                        primaryTypographyProps={{ fontFamily: 'monospace' }}
                        secondary={
                          <Box component="span" sx={{ display: 'block' }}>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              {tool.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                              {tool.isDefault ? (
                                <Chip size="small" label="Default" color="primary" variant="outlined" />
                              ) : null}
                              {tool.isSystem ? (
                                <Chip size="small" label="System" color="secondary" variant="outlined" />
                              ) : null}
                              {!tool.isDefault && !tool.isSystem ? (
                                <Chip size="small" label="Custom" variant="outlined" />
                              ) : null}
                            </Box>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCatalogAttachDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAttachCatalogTools}
            disabled={
              selectedCatalogToolIds.size === 0 ||
              attachingCatalogTools ||
              catalogToolsPickList.length === 0
            }
          >
            {attachingCatalogTools
              ? 'Attaching...'
              : selectedCatalogToolIds.size > 0
                ? `Attach (${selectedCatalogToolIds.size})`
                : 'Attach'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
